import { getImovelStatusLabel } from './imovelPresenter';
import { numberValue } from './primitiveHelpers';
import { formatBRL as formatBRLCurrency } from './currency';

export const formatBRL = value => {
  const parsed = numberValue(value);
  return parsed === null ? '' : formatBRLCurrency(parsed, { fallback: '' });
};

const FALLBACK_STATUS_CLASS = 'bg-slate-50 text-slate-600';
const STATUS_CLASSES = {
  disponivel: 'bg-emerald-50 text-emerald-700',
  available: 'bg-emerald-50 text-emerald-700',
  reservado: 'bg-amber-50 text-amber-700',
  scheduled: 'bg-amber-50 text-amber-700',
  vendido: 'bg-rose-50 text-rose-700',
  alugado: 'bg-rose-50 text-rose-700',
  unavailable: 'bg-rose-50 text-rose-700',
  inativo: 'bg-slate-50 text-slate-500',
};

const TYPE_LABELS = {
  apartamento: 'Apartamento',
  apartment: 'Apartamento',
  casa: 'Casa',
  casa_condominio: 'Casa em condominio',
  house: 'Casa',
  cobertura: 'Cobertura',
  sobrado: 'Sobrado',
  loft: 'Loft',
  studio: 'Studio',
  flat: 'Flat',
  terreno: 'Terreno',
  comercial: 'Comercial',
  commercial: 'Comercial',
  residential: 'Residencial',
  empreendimento: 'Empreendimento',
  sala_comercial: 'Sala comercial',
  office: 'Sala comercial',
  business: 'Loja',
  galpao: 'Galpao',
  chacara: 'Chacara',
};

const TAG_LABELS = {
  novo: 'Anuncio novo',
  'preco-caiu': 'Baixou o preco',
  'preco-desatualizado': 'Preco desatualizado',
  preco_desatualizado: 'Preco desatualizado',
  oldUpdated: 'Preco desatualizado',
  'alta-procura': 'Anuncio com muita procura',
  'pronto-para-visita': 'Agende visita online',
  'oferece-financiamento': 'Financiamento',
  'com-condominio': 'Condominio',
  poucas_fotos: 'Poucas fotos disponiveis',
  insufficientPhotos: 'Poucas fotos disponiveis',
  imagem_qualidade_baixa: 'Qualidade das imagens abaixo do permitido',
  imageQuality: 'Qualidade das imagens abaixo do permitido',
  verified: 'Verificado',
  favorite: 'Favorito',
  classified: 'Classificado',
  recommended: 'Recomendado',
  recently_viewed: 'Visto recentemente',
  rentOnTermination: 'Aluguel em rescisao',
  goodPrice: 'Bom preco',
  rentPriceDecreased: 'Aluguel reduzido',
};

const BOOLEAN_TRUE = ['true', 'sim', 'yes', '1'];
const BOOLEAN_FALSE = ['false', 'nao', 'no', '0'];

export const valueFrom = (item, ...keys) => {
  if (!item) return '';
  const attrs = item.additionalAttributes || item.additional_attributes || {};
  const characteristics = item.caracteristicas || item.characteristics || {};
  const match = keys.find(
    key =>
      item[key] != null || attrs[key] != null || characteristics[key] != null
  );
  if (!match) return '';
  return item[match] ?? attrs[match] ?? characteristics[match] ?? '';
};

const booleanValue = value => {
  if (value === true || value === false) return value;
  const normalized = String(value ?? '').toLowerCase();
  if (BOOLEAN_TRUE.includes(normalized)) return true;
  if (BOOLEAN_FALSE.includes(normalized)) return false;
  return false;
};

const mediaUrl = item => {
  if (!item) return '';
  if (typeof item === 'string') return item;
  return item.url || item.src || item.previewUrl || item.thumbUrl || '';
};

const mediaItemsFor = imovel => {
  const media =
    valueFrom(imovel, 'media') ||
    valueFrom(imovel, 'midias') ||
    valueFrom(imovel, 'photos') ||
    valueFrom(imovel, 'images') ||
    [];

  const photos = valueFrom(media, 'photos') || media;
  return Array.isArray(photos) ? photos : [];
};

const firstMediaUrl = imovel =>
  valueFrom(imovel, 'coverPhoto', 'cover_photo') ||
  mediaUrl(mediaItemsFor(imovel)[0]);

export const codeForListing = imovel =>
  valueFrom(
    imovel,
    'codigo',
    'code',
    'propertyCode',
    'property_code',
    'externalId',
    'external_id',
    'houseId',
    'house_id'
  ) || valueFrom(imovel, 'id');

export const publicUrlForListing = imovel =>
  valueFrom(
    imovel,
    'linkExterno',
    'link_externo',
    'externalUrl',
    'external_url',
    'publicUrl',
    'public_url',
    'contactWebsite',
    'url'
  );

export const detailIdForListing = (imovel, catalogMode = 'properties') =>
  String(
    catalogMode === 'empreendimentos'
      ? valueFrom(imovel, 'id')
      : codeForListing(imovel) || valueFrom(imovel, 'id')
  );

const businessContextFor = imovel => {
  const context = valueFrom(imovel, 'businessContext', 'business_context');
  if (['purchase', 'comprar', 'venda', 'sale'].includes(String(context))) {
    return 'purchase';
  }
  if (['rental', 'alugar', 'aluguel', 'rent'].includes(String(context))) {
    return 'rental';
  }

  return valueFrom(imovel, 'finalidade') === 'venda' ? 'purchase' : 'rental';
};

const businessContextLabelFor = context =>
  context === 'purchase' ? 'Venda' : 'Aluguel';

const typeLabelFor = imovel => {
  const type = valueFrom(imovel, 'type', 'tipo');
  return valueFrom(imovel, 'tipoLabel') || TYPE_LABELS[type] || type || '-';
};

const listingTagsFor = imovel => {
  const direct = valueFrom(imovel, 'listingTags', 'listing_tags');
  return Array.isArray(direct) ? direct : [];
};

const tagPayload = (key, kind = 'neutral') => ({
  key,
  label: TAG_LABELS[key] || key,
  kind,
});

const flagsFor = imovel => {
  const flags = [
    ['verified', booleanValue(valueFrom(imovel, 'verified'))],
    ['favorite', booleanValue(valueFrom(imovel, 'favorite', 'favorito'))],
    ['classified', booleanValue(valueFrom(imovel, 'classified'))],
    [
      'recommended',
      Boolean(valueFrom(imovel, 'recommendationId', 'recommendation_id')),
    ],
    [
      'recently_viewed',
      booleanValue(valueFrom(imovel, 'recentlyViewed', 'recently_viewed')),
    ],
    [
      'rentOnTermination',
      booleanValue(
        valueFrom(imovel, 'rentOnTermination', 'rent_on_termination')
      ),
    ],
    ['goodPrice', booleanValue(valueFrom(imovel, 'goodPrice', 'good_price'))],
    [
      'rentPriceDecreased',
      booleanValue(
        valueFrom(imovel, 'rentPriceDecreased', 'rent_price_decreased')
      ),
    ],
  ];

  return flags
    .filter(([, enabled]) => enabled)
    .map(([key]) => tagPayload(key, 'flag'));
};

const priceValueFor = (imovel, context) =>
  context === 'purchase'
    ? valueFrom(imovel, 'salePrice', 'sale_price', 'valorVenda', 'valor_venda')
    : valueFrom(
        imovel,
        'rentPrice',
        'rent_price',
        'valorAluguel',
        'valor_aluguel'
      );

const totalCostFor = imovel =>
  valueFrom(imovel, 'totalCost', 'total_cost', 'totalMonthlyCost');

const pricePayloadFor = (imovel, context) => {
  const priceValue = priceValueFor(imovel, context);
  const totalCost = totalCostFor(imovel);
  const formattedPrice = formatBRL(priceValue);
  const formattedTotal = formatBRL(totalCost);
  const priceLabel =
    valueFrom(imovel, 'priceLabel', 'price_label') ||
    (context === 'purchase' ? 'Valor de venda' : 'Aluguel mensal');

  return {
    value: priceValue,
    formatted: formattedPrice || 'Preco sob consulta',
    label: priceLabel,
    headline: `${priceLabel}: ${formattedPrice || 'Preco sob consulta'}`,
    total: totalCost,
    totalFormatted: formattedTotal,
    totalLabel: formattedTotal ? `Total estimado: ${formattedTotal}` : '',
    monthlySuffix: context === 'rental' && formattedPrice ? '/mes' : '',
    priceOnRequest: !formattedPrice,
  };
};

const locationLabelFor = imovel =>
  [
    valueFrom(imovel, 'street', 'endereco', 'address'),
    valueFrom(imovel, 'neighborhood', 'bairro'),
    valueFrom(imovel, 'city', 'cidade'),
    valueFrom(imovel, 'state', 'uf'),
  ]
    .filter(Boolean)
    .join(', ') || 'Sem informacoes do endereco';

const detailsLabelFor = imovel => {
  const area = numberValue(
    valueFrom(
      imovel,
      'area',
      'areaUtil',
      'area_util',
      'areaTotal',
      'area_total'
    )
  );
  const bedrooms = numberValue(valueFrom(imovel, 'bedrooms', 'quartos'));
  const parking = numberValue(
    valueFrom(
      imovel,
      'parkingSpots',
      'parking_spots',
      'vagasGaragem',
      'vagas_garagem'
    )
  );

  return [
    area ? `${area} m2` : null,
    bedrooms != null
      ? `${bedrooms} ${bedrooms === 1 ? 'quarto' : 'quartos'}`
      : null,
    parking != null ? `${parking} ${parking === 1 ? 'vaga' : 'vagas'}` : null,
  ]
    .filter(Boolean)
    .join(' - ');
};

const inventoryLabelFor = imovel => {
  const available = valueFrom(imovel, 'availableUnits', 'available_units');
  const total = valueFrom(imovel, 'totalUnits', 'total_units');
  if (available === '' && total === '') return '';
  if (available !== '' && total !== '') return `${available}/${total} unidades`;
  if (available !== '') return `${available} unidades disponiveis`;
  return `${total} unidades totais`;
};

export const normalizeListingCard = (
  imovel,
  { catalogMode = 'properties', selected = false, position = 0 } = {}
) => {
  const context = businessContextFor(imovel);
  const price = pricePayloadFor(imovel, context);
  const status = valueFrom(imovel, 'status') || '';
  const mediaItems = mediaItemsFor(imovel);
  const coverPhoto = firstMediaUrl(imovel);
  const title =
    valueFrom(imovel, 'title', 'titulo', 'name') || 'Imovel sem titulo';
  const id =
    valueFrom(imovel, 'id', 'externalId', 'external_id') ||
    codeForListing(imovel);
  const listingTags = listingTagsFor(imovel).map(tagPayload);
  const flags = flagsFor(imovel);
  const unavailable =
    booleanValue(valueFrom(imovel, 'unavailable')) ||
    (status && !['disponivel', 'available'].includes(status));

  return {
    id,
    raw: imovel,
    position,
    selected,
    catalogMode,
    code: codeForListing(imovel),
    detailId: detailIdForListing(imovel, catalogMode),
    title,
    subtitle: valueFrom(
      imovel,
      'shortDescription',
      'short_description',
      'descricao'
    ),
    typeLabel: typeLabelFor(imovel),
    businessContext: context,
    businessContextLabel: businessContextLabelFor(context),
    status,
    statusLabel: getImovelStatusLabel(status),
    statusClass: STATUS_CLASSES[status] || FALLBACK_STATUS_CLASS,
    unavailable,
    location: locationLabelFor(imovel),
    details: detailsLabelFor(imovel),
    price,
    tags: [...flags, ...listingTags],
    inventoryLabel: inventoryLabelFor(imovel),
    coverPhoto,
    photos: mediaItems,
    photoAlt:
      valueFrom(imovel, 'altText', 'alt_text') ||
      [typeLabelFor(imovel), detailsLabelFor(imovel), locationLabelFor(imovel)]
        .filter(Boolean)
        .join(', '),
    latitude: numberValue(valueFrom(imovel, 'latitude', 'lat')),
    longitude: numberValue(valueFrom(imovel, 'longitude', 'lng')),
    publicUrl: publicUrlForListing(imovel),
    cta: valueFrom(imovel, 'cta') || {},
    recommendationTrackingData: {
      recommendationId: valueFrom(
        imovel,
        'recommendationId',
        'recommendation_id'
      ),
      recommendationType: valueFrom(
        imovel,
        'recommendationType',
        'recommendation_type'
      ),
      deviceId: valueFrom(imovel, 'deviceId', 'device_id'),
    },
  };
};

export const normalizeListingCards = (
  items,
  { catalogMode = 'properties', selectedIds = new Set() } = {}
) =>
  (items || []).map((item, index) =>
    normalizeListingCard(item, {
      catalogMode,
      selected: selectedIds.has(valueFrom(item, 'id')),
      position: index + 1,
    })
  );

export const listingCardTrackingPayload = (card, extras = {}) => ({
  listingId: card.id,
  listingCode: card.code,
  businessContext: card.businessContext,
  catalog: card.catalogMode,
  position: card.position,
  cidade: valueFrom(card.raw, 'cidade', 'city'),
  bairro: valueFrom(card.raw, 'bairro', 'neighborhood'),
  ...card.recommendationTrackingData,
  ...extras,
});

export const withShareUtm = (
  url,
  { medium = 'copy_share', businessContext = 'rental' } = {}
) => {
  if (!url) return '';

  try {
    const origin =
      typeof window === 'undefined'
        ? 'https://imudei.app'
        : window.location.origin;
    const parsed = new URL(url, origin);
    parsed.searchParams.set('utm_source', 'shared');
    parsed.searchParams.set('utm_medium', medium);
    parsed.searchParams.set('utm_campaign', businessContext);
    return parsed.toString();
  } catch {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}utm_source=shared&utm_medium=${medium}&utm_campaign=${businessContext}`;
  }
};
